import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useWorkforceTelemetry from '../../hooks/useWorkforceTelemetry';
import WorkspacePage from '../../components/layout/WorkspacePage';
import Card from '../../components/ui/Card';
import Pill from '../../components/ui/Pill';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import EmptyState from '../../components/ui/EmptyState';
import StatusBanner from '../../components/ui/StatusBanner';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { useEmployeeDirectory } from '../../hooks/useEmployeeDirectory';
import { useAccessibleLocations } from '../../hooks/useAccessibleLocations';
import { apiRequest } from '../../services/apiClient';
import { formatRoleLabel } from '../../config/appRoutes';
import { buildPersonWorkspacePath } from '../../config/workforceRouting';
import ExpiringDocumentsPanel from './ExpiringDocumentsPanel';
import EmployeeProfilePage from './EmployeeProfilePage';
import ImportEmployeesModal from './ImportEmployeesModal';
import './EmployeeProfilePage.css';

const EMPLOYMENT_LABELS = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  seasonal: 'Seasonal',
  intern: 'Intern'
};

const DIRECTORY_ROLE_FILTER_OPTIONS = [
  { value: 'director', label: formatRoleLabel('director') },
  { value: 'admin', label: formatRoleLabel('admin') },
  { value: 'airport_manager', label: formatRoleLabel('airport_manager') },
  { value: 'ops_manager', label: formatRoleLabel('ops_manager') }
];

function hasEmployeePermission(user, permission) {
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  return permissions.includes(permission) || permissions.includes('manage_users');
}

function fullName(row) {
  // Workforce 2.0 display fallback:
  //   preferred_name → first_name + last_name → manager name → email/id.
  return row.preferredName
    || [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
    || row.displayName
    || [row.managerFirstName, row.managerLastName].filter(Boolean).join(' ').trim()
    || row.email
    || (row.profileId ? `Employee #${row.profileId}` : 'Employee');
}

function DirectoryFilters({ value, onChange, locations, supervisorOptions = [] }) {
  const set = (key) => (event) => onChange({ ...value, [key]: event.target.value });
  return (
    <div className="employee-directory__filters" role="search" aria-label="Filter directory">
      <Input
        placeholder="Search by name or email"
        value={value.q || ''}
        onChange={set('q')}
        aria-label="Search employees"
        data-testid="directory-search-input"
      />
      <Select value={value.employmentType || ''} onChange={set('employmentType')} aria-label="Employment type">
        <option value="">All employment types</option>
        {Object.entries(EMPLOYMENT_LABELS).map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </Select>
      <Select value={value.role || ''} onChange={set('role')} aria-label="Role">
        <option value="">All roles</option>
        {DIRECTORY_ROLE_FILTER_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
        <option value="unassigned">No operational role</option>
      </Select>
      <Select
        value={String(value.supervisorEmployeeId || '')}
        onChange={set('supervisorEmployeeId')}
        aria-label="Supervisor"
        data-testid="directory-filter-supervisor"
      >
        <option value="">All supervisors</option>
        <option value="unassigned">Unassigned</option>
        {supervisorOptions.map((option) => (
          <option key={option.id} value={String(option.id)}>{option.label}</option>
        ))}
      </Select>
      <Select value={value.active || 'active'} onChange={set('active')} aria-label="Active status">
        <option value="active">Active workforce</option>
        <option value="inactive">Inactive only</option>
        <option value="any">Active + inactive</option>
      </Select>
      <Select value={value.access || ''} onChange={set('access')} aria-label="Access status">
        <option value="">All access states</option>
        <option value="linked">Access linked</option>
        <option value="unlinked">Access unlinked</option>
        <option value="active">Active access</option>
        <option value="inactive">Inactive access</option>
      </Select>
      <Select value={value.certification || ''} onChange={set('certification')} aria-label="Certification status">
        <option value="">All certifications</option>
        <option value="expiring">Expiring/expired</option>
        <option value="clear">No expiring certs</option>
      </Select>
      {/* Workforce People Model Phase 3.2 — classification + department filters. */}
      <Select
        value={value.employmentClassification || ''}
        onChange={set('employmentClassification')}
        aria-label="Classification"
        data-testid="directory-filter-classification"
      >
        <option value="">All classifications</option>
        <option value="hourly">Hourly</option>
        <option value="salary">Salary</option>
        <option value="unset">Not set</option>
      </Select>
      <Input
        placeholder="Filter by department"
        value={value.department || ''}
        onChange={set('department')}
        aria-label="Department"
        data-testid="directory-filter-department"
      />
      <Select
        value={String(value.locationId || '')}
        onChange={set('locationId')}
        aria-label="Location"
        disabled={!Array.isArray(locations) || locations.length === 0}
      >
        <option value="">
          {Array.isArray(locations) && locations.length > 0 ? 'All locations' : 'No locations available'}
        </option>
        {(locations || []).map(loc => (
          <option key={loc.id} value={String(loc.id)}>{loc.code} · {loc.name}</option>
        ))}
      </Select>
      <Select value={value.archived || 'false'} onChange={set('archived')} aria-label="Archived filter">
        <option value="false">Active only</option>
        <option value="any">Include archived</option>
        <option value="true">Archived only</option>
      </Select>
    </div>
  );
}

function roleForRow(row) {
  return row.role || row.managerRoleKey || '';
}

function roleTone(role) {
  if (role === 'director' || role === 'admin') return 'primary';
  if (role === 'airport_manager') return 'info';
  return 'neutral';
}

// Workforce People Model Phase 3.2 — classification cell.
// Renders the schema-canonical 'hourly'/'salary' value as a Pill;
// null / missing values fall back to the em-dash placeholder.
const CLASSIFICATION_LABELS = Object.freeze({
  hourly: 'Hourly',
  salary: 'Salary'
});
function ClassificationPill({ value }) {
  if (!value) {
    return <span aria-label="No classification">—</span>;
  }
  const label = CLASSIFICATION_LABELS[value] || value;
  const tone = value === 'salary' ? 'info' : 'neutral';
  return <Pill variant={tone}>{label}</Pill>;
}

function DirectoryRow({ row, canDeepLink, isSelected = false, onView }) {
  const isArchived = Boolean(row.archivedAt);
  const role = roleForRow(row);
  const isActive = row.activeStatus !== 'inactive';
  const hasAccess = row.accessStatus === 'linked' || row.accessStatus === 'active' || row.accessStatus === 'inactive';
  const certExpiring = row.certificationStatus === 'expiring';
  const selectable = canDeepLink && Boolean(row.profileId || row.managerId);
  return (
    <tr
      key={row.id}
      className={`employee-directory__row${isSelected ? ' is-selected' : ''}${selectable ? ' is-selectable' : ''}`}
      onClick={selectable ? () => onView(row) : undefined}
      data-testid={isSelected ? 'employee-directory-selected-row' : undefined}
    >
      <td>
        <div style={{ fontWeight: 600 }}>{fullName(row)}</div>
        <div style={{ fontSize: 12, color: '#5b6471' }}>
          {row.managerId ? `Roster record #${row.managerId}` : (row.email || 'No roster record')}
        </div>
      </td>
      {/* People Model Phase 4A — operator-facing Employee ID. The
          column is separate from the internal Roster # so operators
          can scan the human-readable identifier without confusing
          it with the relationship key. */}
      <td data-testid={`directory-row-employee-number-${row.id}`}>
        {row.employeeNumber || <span aria-label="No employee id">—</span>}
      </td>
      <td>{row.managerLocationCode || '—'}</td>
      <td>
        <span className="employee-directory__pill-stack">
          {role ? <Pill variant={roleTone(role)}>{formatRoleLabel(role)}</Pill> : <Pill variant="neutral">No role</Pill>}
          {row.employmentType ? <Pill variant="neutral">{EMPLOYMENT_LABELS[row.employmentType] || row.employmentType}</Pill> : null}
        </span>
      </td>
      {/* Workforce People Model Phase 3.2 — Phase 1/2 person-level fields. */}
      <td data-testid={`directory-row-job-title-${row.id}`}>
        {row.jobTitle || <span aria-label="No job title">—</span>}
      </td>
      <td data-testid={`directory-row-department-${row.id}`}>
        {row.department || <span aria-label="No department">—</span>}
      </td>
      <td data-testid={`directory-row-classification-${row.id}`}>
        <ClassificationPill value={row.employmentClassification} />
      </td>
      <td>
        <span className="employee-directory__pill-stack">
          <Pill variant={isActive ? 'success' : 'warning'}>{isActive ? 'Active' : 'Inactive'}</Pill>
          <Pill variant={hasAccess ? 'success' : 'warning'}>
            {hasAccess ? (row.userIsActive === false ? 'Access inactive' : 'Access linked') : 'Access unlinked'}
          </Pill>
          {certExpiring ? <Pill variant="danger">Certification expiring</Pill> : <Pill variant="neutral">Certs clear</Pill>}
          {row.profileCompletenessScore !== null && row.profileCompletenessScore !== undefined ? (
            <span>{row.profileCompletenessScore}% profile</span>
          ) : null}
          {isArchived ? <Pill variant="warning">Archived</Pill> : null}
          {row._piiRedacted ? <Pill variant="neutral">PII restricted</Pill> : null}
          {row.permissions?.isSelfService ? <Pill variant="info">You</Pill> : null}
        </span>
      </td>
      <td style={{ textAlign: 'right' }}>
        {selectable ? (
          <Button
            size="sm"
            variant={isSelected ? 'primary' : 'secondary'}
            onClick={(event) => {
              event.stopPropagation();
              onView(row);
            }}
          >
            {isSelected ? 'Selected' : 'Open workspace'}
          </Button>
        ) : row.userId ? (
          <a className="ui-button ui-button--sm ui-button--secondary" href="/users">View access</a>
        ) : null}
      </td>
    </tr>
  );
}

// Workforce 2.0: "Add Employee" creates an Employee first. Leadership is
// an optional assignment, not the identity. role defaults to 'employee'
// (standalone profile); only a leadership role creates a managers row.
const ADD_EMPLOYEE_EMPTY = {
  firstName: '',
  lastName: '',
  preferredName: '',
  email: '',
  employeeId: '',
  locationId: '',
  role: 'employee',
  managerRoleKey: 'ops_manager',
  roleTitle: '',
  reportsToProfileId: '',
  employmentType: '',
  dateOfHire: '',
  createAccess: false
};

// Org roles that are leadership assignments — these create a managers
// (roster) record and keep the existing manager-creation flow. Everyone
// else is a standalone employee_profile.
const LEADERSHIP_ROLES = new Set(['manager', 'director']);
const ADD_EMPLOYEE_ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'manager', label: 'Manager' },
  { value: 'director', label: 'Director' }
];
// Default roster role (managers.manager_role_key) for a leadership org role.
const LEADERSHIP_ROSTER_DEFAULT = { manager: 'ops_manager', director: 'director' };

function AddEmployeeModal({ locations, profiles, onClose, onCreated }) {
  const [form, setForm] = useState(ADD_EMPLOYEE_EMPTY);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const activeLocations = (locations || []).filter(location => location.isActive !== false);
  const isLeadership = LEADERSHIP_ROLES.has(form.role);

  const set = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(current => ({ ...current, [key]: value }));
  };

  // Changing the role flips leadership on/off. When switching INTO
  // leadership, seed a sensible roster role; switching out clears the
  // leadership-only roster role so it can't leak into a standalone POST.
  const setRole = (event) => {
    const role = event.target.value;
    setForm(current => ({
      ...current,
      role,
      managerRoleKey: LEADERSHIP_ROLES.has(role)
        ? (LEADERSHIP_ROSTER_DEFAULT[role] || 'ops_manager')
        : current.managerRoleKey
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    // Workforce 2.0: only first/last/location are universally required.
    // Role title is a leadership-only field.
    if (!form.firstName.trim() || !form.lastName.trim() || !form.locationId) {
      setError('First name, last name, and location are required.');
      return;
    }
    if (isLeadership && !form.roleTitle.trim()) {
      setError('Role title is required for leadership roles.');
      return;
    }
    if (form.createAccess && !form.email.trim()) {
      setError('Work email is required when creating user access.');
      return;
    }

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const preferredName = form.preferredName.trim();

    setIsSaving(true);
    try {
      if (isLeadership) {
        // Leadership assignment — existing manager-creation flow, unchanged
        // except that the linked profile now also records the org role.
        const today = new Date().toISOString().slice(0, 10);
        // Employee ID and Manager Key are generated server-side when
        // omitted — only include employeeId if the operator typed one.
        const manager = await apiRequest('/api/managers', {
          method: 'POST',
          body: JSON.stringify({
            ...(form.employeeId.trim() ? { employeeId: form.employeeId.trim() } : {}),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.createAccess ? form.email.trim() : '',
            locationId: Number(form.locationId),
            managerRoleKey: form.managerRoleKey,
            roleTitle: form.roleTitle.trim(),
            department: '',
            hireDate: form.dateOfHire || null,
            activeFrom: today
          })
        });

        const profile = await apiRequest('/api/employees/profiles', {
          method: 'POST',
          body: JSON.stringify({
            managerId: manager.id,
            userId: manager.accessProvisioning?.user?.id || null,
            role: form.role,
            firstName: firstName || null,
            lastName: lastName || null,
            preferredName: preferredName || null,
            personalEmail: form.email.trim() || null,
            dateOfHire: form.dateOfHire || null,
            employmentType: form.employmentType || null
          })
        });

        await onCreated?.({ manager, profile });
        return;
      }

      // Standalone employee — no managers row. Names are stored in the
      // canonical first_name / last_name (+ optional preferred_name)
      // fields. Employee ID auto-generates when omitted.
      const body = {
        role: form.role,
        status: 'active',
        locationId: Number(form.locationId),
        firstName: firstName || null,
        lastName: lastName || null,
        preferredName: preferredName || null,
        personalEmail: form.email.trim() || null,
        dateOfHire: form.dateOfHire || null,
        employmentType: form.employmentType || null
      };
      if (form.employeeId.trim()) {
        body.employeeNumber = form.employeeId.trim();
      }
      if (form.reportsToProfileId) {
        body.supervisorEmployeeId = Number(form.reportsToProfileId);
      }

      const profile = await apiRequest('/api/employees/profiles', {
        method: 'POST',
        body: JSON.stringify(body)
      });

      await onCreated?.({ profile });
    } catch (err) {
      setError(err?.message || 'Unable to add employee.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="employee-hub-modal-backdrop" role="presentation">
      <div className="employee-hub-modal" role="dialog" aria-modal="true" aria-labelledby="add-employee-title">
        <div className="employee-hub-modal__header">
          <div>
            <h2 id="add-employee-title">Add Employee</h2>
            <p>Every person is an employee first. Choose a leadership role only when this person manages a roster.</p>
          </div>
          <button type="button" className="employee-hub-modal__close" onClick={onClose} aria-label="Close add employee">×</button>
        </div>
        {error ? <StatusBanner kind="error" message={error} live /> : null}
        <form className="employee-profile-form" onSubmit={submit}>
          <div className="employee-profile-form__grid">
            <label>
              First name
              <Input value={form.firstName} onChange={set('firstName')} required />
            </label>
            <label>
              Last name
              <Input value={form.lastName} onChange={set('lastName')} required />
            </label>
            <label>
              Preferred name
              <Input value={form.preferredName} onChange={set('preferredName')} />
            </label>
            <label>
              Employee ID
              <Input value={form.employeeId} onChange={set('employeeId')} placeholder="Optional; generated if blank" />
            </label>
            <label>
              Location assignment
              <Select value={form.locationId} onChange={set('locationId')} required>
                <option value="">Select location</option>
                {activeLocations.map(location => (
                  <option key={location.id} value={location.id}>{location.code} · {location.name}</option>
                ))}
              </Select>
            </label>
            <label>
              Role
              <Select value={form.role} onChange={setRole} data-testid="add-employee-role">
                {ADD_EMPLOYEE_ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </label>
            {!isLeadership ? (
              <label>
                Reports to
                <Select
                  value={form.reportsToProfileId}
                  onChange={set('reportsToProfileId')}
                  data-testid="add-employee-reports-to"
                >
                  <option value="">No supervisor (optional)</option>
                  {(profiles || [])
                    .filter(p => p && p.profileId)
                    .map(p => (
                      <option key={p.profileId} value={p.profileId}>
                        {p.name || p.preferredName || `Profile #${p.profileId}`}
                      </option>
                    ))}
                </Select>
              </label>
            ) : null}
            {isLeadership ? (
              <>
                <label>
                  Roster role
                  <Select value={form.managerRoleKey} onChange={set('managerRoleKey')} data-testid="add-employee-roster-role">
                    <option value="ops_manager">{formatRoleLabel('ops_manager')}</option>
                    <option value="airport_manager">{formatRoleLabel('airport_manager')}</option>
                    <option value="director">Director</option>
                  </Select>
                </label>
                <label>
                  Role title
                  <Input value={form.roleTitle} onChange={set('roleTitle')} required placeholder="Supervisor, GM, Lead, etc." />
                </label>
              </>
            ) : null}
            <label>
              Hire date
              <Input type="date" value={form.dateOfHire} onChange={set('dateOfHire')} />
            </label>
            <label>
              Employment type
              <Select value={form.employmentType} onChange={set('employmentType')}>
                <option value="">Not set</option>
                {Object.entries(EMPLOYMENT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </label>
            <label>
              Work email
              <Input type="email" value={form.email} onChange={set('email')} placeholder="Needed for user access" />
            </label>
            <label className="employee-profile-form__field--full employee-hub-modal__checkbox">
              <input type="checkbox" checked={form.createAccess} onChange={set('createAccess')} />
              Create or link user access from this work email
            </label>
          </div>
          <div className="employee-hub-modal__actions">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>Cancel</Button>
            <Button type="submit" loading={isSaving} loadingLabel="Creating…">Create employee</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function normalizeWorkforceRows({ profileRows, managers, users, expiringCerts, search }) {
  const usersByManagerId = new Map();
  const usersByEmployeeProfileId = new Map();
  for (const user of users || []) {
    if (user.managerId) usersByManagerId.set(Number(user.managerId), user);
    if (user.employeeProfileId) usersByEmployeeProfileId.set(Number(user.employeeProfileId), user);
  }
  const certsByManagerId = new Map();
  for (const cert of expiringCerts || []) {
    if (!cert.managerId) continue;
    const current = certsByManagerId.get(Number(cert.managerId)) || 0;
    certsByManagerId.set(Number(cert.managerId), current + 1);
  }
  const managersById = new Map((managers || []).map(manager => [Number(manager.id), manager]));
  const rows = [];
  const seenManagerIds = new Set();
  const seenUserIds = new Set();

  for (const profile of profileRows || []) {
    const manager = managersById.get(Number(profile.managerId)) || {};
    // users.employee_profile_id is authoritative. Never infer an employee's
    // login from the legacy profile mirror or manager relationship.
    const linkedUser = usersByEmployeeProfileId.get(Number(profile.id));
    if (profile.managerId) seenManagerIds.add(Number(profile.managerId));
    if (linkedUser?.id) seenUserIds.add(Number(linkedUser.id));
    rows.push({
      ...profile,
      profileId: profile.id,
      id: `profile-${profile.id}`,
      source: 'profile',
      displayName: fullName(profile),
      managerRoleKey: manager.managerRoleKey || linkedUser?.role || null,
      role: linkedUser?.role || manager.managerRoleKey || null,
      managerLocationCode: profile.managerLocationCode || manager.locationCode || linkedUser?.locationCode || '',
      activeStatus: manager.status === 'inactive' || profile.archivedAt ? 'inactive' : 'active',
      accessStatus: linkedUser ? (linkedUser.isActive ? 'active' : 'inactive') : 'unlinked',
      userIsActive: linkedUser?.isActive,
      userId: linkedUser?.id || null,
      email: profile.managerEmail || linkedUser?.email || '',
      certificationStatus: certsByManagerId.has(Number(profile.managerId)) ? 'expiring' : 'clear'
    });
  }

  for (const manager of managers || []) {
    if (seenManagerIds.has(Number(manager.id))) continue;
    const linkedUser = usersByManagerId.get(Number(manager.id));
    if (linkedUser?.id) seenUserIds.add(Number(linkedUser.id));
    rows.push({
      id: `manager-${manager.id}`,
      source: 'manager',
      managerId: manager.id,
      managerFirstName: manager.firstName,
      managerLastName: manager.lastName,
      displayName: [manager.firstName, manager.lastName].filter(Boolean).join(' ').trim(),
      managerLocationCode: manager.locationCode,
      employmentType: '',
      profileCompletenessScore: null,
      managerRoleKey: manager.managerRoleKey,
      role: linkedUser?.role || manager.managerRoleKey,
      activeStatus: manager.status === 'inactive' ? 'inactive' : 'active',
      accessStatus: linkedUser ? (linkedUser.isActive ? 'active' : 'inactive') : 'unlinked',
      userIsActive: linkedUser?.isActive,
      userId: linkedUser?.id || null,
      email: manager.email || linkedUser?.email || '',
      certificationStatus: certsByManagerId.has(Number(manager.id)) ? 'expiring' : 'clear'
    });
  }

  for (const user of users || []) {
    if (seenUserIds.has(Number(user.id))) continue;
    rows.push({
      id: `user-${user.id}`,
      source: 'user',
      managerId: user.managerId || null,
      displayName: user.username || user.email,
      managerLocationCode: user.locationCode || summarizeUserLocations(user),
      employmentType: '',
      profileCompletenessScore: null,
      role: user.role,
      activeStatus: user.isActive ? 'active' : 'inactive',
      accessStatus: user.isActive ? 'active' : 'inactive',
      userIsActive: user.isActive,
      userId: user.id,
      email: user.email,
      certificationStatus: user.managerId && certsByManagerId.has(Number(user.managerId)) ? 'expiring' : 'clear'
    });
  }

  const q = String(search || '').trim().toLowerCase();
  return q ? rows.filter(row => (
    fullName(row).toLowerCase().includes(q)
    || String(row.email || '').toLowerCase().includes(q)
    || String(row.managerLocationCode || '').toLowerCase().includes(q)
  )) : rows;
}

function summarizeUserLocations(user) {
  const codes = Array.from(new Set([
    user?.locationCode || null,
    ...((user?.assignedLocations || []).map(location => location.locationCode || null))
  ].filter(Boolean)));
  return codes.join(', ');
}

function selectionFromRow(row) {
  if (!row) return null;
  if (row.profileId || row.source === 'profile') {
    return {
      profileId: Number(row.profileId || row.id) || null,
      managerId: Number(row.managerId) || null
    };
  }
  if (row.managerId) {
    return {
      profileId: null,
      managerId: Number(row.managerId)
    };
  }
  return null;
}

function sameSelection(left, right) {
  return String(left?.profileId || '') === String(right?.profileId || '')
    && String(left?.managerId || '') === String(right?.managerId || '');
}

const FOCUS_COPY = {
  '/employees': {
    title: 'People & Talent',
    description: 'Search the workforce, select a person, and manage profile, contact, access, documents, and certifications from one hub.'
  },
  '/employees/profiles': {
    title: 'People & Talent',
    description: 'Employee Profiles now live inside the unified People & Talent workspace.'
  },
  '/employees/documents': {
    title: 'People & Talent',
    description: 'Documents are managed from the selected employee hub.'
  },
  '/employees/expiring': {
    title: 'People & Talent',
    description: 'Review certification status, then manage expirations from the selected employee hub.'
  }
};

/**
 * Normalize `selectedManagerIdExternal` into a stable string id (or '').
 *
 * Accepted shapes:
 *   - number  (e.g. workforceRoute.employeeId) → '31'
 *   - string  (e.g. '31')                      → '31'
 *   - object with .id field (e.g. a manager record {id: '1'}) → '1'
 *     This is the defensive branch — production logs caught a caller
 *     passing a manager record instead of an id, which made
 *     `String({id:'1'})` render as the literal "[object Object]" and
 *     broke every downstream `row.managerId === selectedManagerId`
 *     comparison.
 *   - null / undefined / 0 / ''                → ''
 *
 * The URL-derived workforceRoute.employeeId is the canonical source.
 * Whenever an operator lands on /workforce/employees/:employeeId the
 * URL wins, regardless of what other state might be in play.
 */
function coerceManagerIdFromExternal(value) {
  if (value == null) return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? String(value) : '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value.id != null) {
    return String(value.id);
  }
  return '';
}

export default function EmployeeDirectoryPage({
  user = null,
  locations: locationsProp = null,
  defaultExpiringExpanded = false,
  onNavigate = null,
  selectedManagerIdExternal = null,
  workspaceFocus = '/employees',
  // W1: when true, emit /workforce/employees/:id on selection so the
  // operator stays inside the canonical URL family they arrived on.
  // When false (default), legacy /employees/profile/:id is emitted —
  // matching the existing behavior for /employees-rooted bookmarks.
  useCanonicalUrls = false
}) {
  const navigate = useNavigate();
  const { recordEvent } = useWorkforceTelemetry();
  const focusCopy = FOCUS_COPY[workspaceFocus] || {
    title: 'Employee directory',
    description: 'Search and filter across the active workforce. PII is only visible to permitted users.'
  };
  const [filters, setFilters] = useState({
    q: '',
    employmentType: '',
    supervisorEmployeeId: '',
    locationId: '',
    archived: 'false',
    limit: 25,
    role: '',
    active: 'active',
    access: '',
    certification: '',
    // Workforce People Model Phase 3.2 — classification + department.
    employmentClassification: '',
    department: ''
  });
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [createBanner, setCreateBanner] = useState('');
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  // Defensive coercion: `selectedManagerIdExternal` is documented as a
  // numeric employee/manager id, but production logs revealed callers
  // occasionally pass an object shaped like { id: '1' } (a manager
  // record leaking into the prop). `String({id:'1'})` yields the
  // literal "[object Object]", which then fails every downstream
  // string comparison and the embedded profile never mounts. Normalize
  // here once so every consumer sees a stable string id (or '').
  const externalManagerId = coerceManagerIdFromExternal(selectedManagerIdExternal);
  const [selectedEmployee, setSelectedEmployee] = useState(
    externalManagerId ? { profileId: null, managerId: Number(externalManagerId) } : null
  );
  const [workforceOverlay, setWorkforceOverlay] = useState({ managers: [], users: [], expiringCerts: [] });
  const [overlayError, setOverlayError] = useState('');
  const { items, isLoading, error, status, loadMore, nextCursor, refetch } = useEmployeeDirectory(filters);
  const accessibleLocationsResult = useAccessibleLocations();
  // Prop overrides the hook so tests can inject deterministic data
  // without mocking the API; production callers leave the prop undefined.
  const locations = Array.isArray(locationsProp) ? locationsProp : accessibleLocationsResult.locations;

  const canDeepLink = useMemo(
    () => hasEmployeePermission(user, 'employees.read'),
    [user]
  );
  const canAddEmployee = useMemo(
    () => hasEmployeePermission(user, 'employees.write')
      && Array.isArray(user?.permissions)
      && user.permissions.includes('manage_users'),
    [user]
  );
  const canReadDocuments = useMemo(
    () => hasEmployeePermission(user, 'employees.documents.read'),
    [user]
  );

  // Telemetry: emit a route.visit each time the workspace mounts at
  // a new focus URL. `workspaceFocus` is one of the canonical paths;
  // the service maps that to a subtype + a `focus` property.
  useEffect(() => {
    const subtypeMap = {
      '/employees': 'directory',
      '/employees/profiles': 'profiles',
      '/employees/documents': 'documents',
      '/employees/expiring': 'expiring'
    };
    const eventSubtype = subtypeMap[workspaceFocus] || 'directory';
    const focusProp = eventSubtype === 'directory' ? null : eventSubtype;
    // W6.1: tag every route.visit with which URL family the operator
    // arrived through (canonical /workforce vs legacy /employees).
    // Same component renders both — `useCanonicalUrls` reflects which
    // family the parent App route mounted us under. The server-side
    // validator accepts the property as optional, so events emitted
    // before W6.1 (no urlFamily) continue to normalize unchanged.
    const urlFamily = useCanonicalUrls ? 'canonical' : 'legacy';
    recordEvent({
      eventType: 'route.visit',
      eventSubtype,
      properties: focusProp ? { focus: focusProp, urlFamily } : { urlFamily }
    });
    if (selectedManagerIdExternal) {
      recordEvent({
        eventType: 'employee.select',
        properties: { source: 'deep_link' }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceFocus]);

  useEffect(() => {
    // Mirror the URL-driven external selection into local state so
    // refresh / deep-link / browser back+forward all land on the right
    // employee. When the external selection is cleared (e.g. user
    // navigated back to /employees), clear the local selection too so
    // the empty-state guide reappears in the right pane.
    //
    // Defensive coercion (coerceManagerIdFromExternal) protects against
    // a caller leaking a manager record like {id: '1'} into this prop —
    // the URL-derived workforce route's employeeId is the canonical
    // source and must win whenever it's present.
    const nextManagerId = coerceManagerIdFromExternal(selectedManagerIdExternal);
    setSelectedEmployee(nextManagerId ? { profileId: null, managerId: Number(nextManagerId) } : null);
  }, [selectedManagerIdExternal]);

  useEffect(() => {
    let cancelled = false;
    async function loadOverlay() {
      setOverlayError('');
      try {
        const [managers, users, expiring] = await Promise.all([
          apiRequest('/api/managers?includeInactive=true'),
          apiRequest('/api/users'),
          canReadDocuments
            ? apiRequest('/api/employees/documents/expiring?withinDays=30&includeExpired=true&limit=500')
            : Promise.resolve({ items: [] })
        ]);
        if (cancelled) return;
        setWorkforceOverlay({
          managers: Array.isArray(managers) ? managers : [],
          users: Array.isArray(users) ? users : [],
          expiringCerts: Array.isArray(expiring?.items) ? expiring.items : []
        });
      } catch (err) {
        if (cancelled) return;
        setOverlayError(err?.message || 'Unable to load the full workforce index.');
      }
    }
    if (canDeepLink) loadOverlay();
    return () => { cancelled = true; };
  }, [canDeepLink, canReadDocuments]);

  useEffect(() => {
    if (!canDeepLink) {
      setSupervisorOptions([]);
      return undefined;
    }
    let cancelled = false;
    apiRequest('/api/employees/profiles?archived=false&limit=100')
      .then((data) => {
        if (cancelled) return;
        const nextOptions = (Array.isArray(data?.items) ? data.items : [])
          .map((item) => {
            const id = Number(item.id);
            if (!(id > 0)) return null;
            const displayName = item.preferredName
              || [item.firstName, item.lastName].filter(Boolean).join(' ').trim()
              || [item.managerFirstName, item.managerLastName].filter(Boolean).join(' ').trim()
              || `Employee #${id}`;
            const locationCode = item.managerLocationCode || '';
            return {
              id,
              label: locationCode ? `${displayName} (${locationCode})` : displayName
            };
          })
          .filter(Boolean);
        setSupervisorOptions(nextOptions);
      })
      .catch(() => {
        if (!cancelled) setSupervisorOptions([]);
      });
    return () => { cancelled = true; };
  }, [canDeepLink]);

  const workforceRows = useMemo(() => {
    const allRows = normalizeWorkforceRows({
      profileRows: items,
      managers: workforceOverlay.managers,
      users: workforceOverlay.users,
      expiringCerts: workforceOverlay.expiringCerts,
      search: filters.q
    });
    return allRows.filter(row => {
      if (filters.supervisorEmployeeId) {
        if (row.source !== 'profile') {
          return false;
        }
        if (filters.supervisorEmployeeId === 'unassigned') {
          if (row.supervisorEmployeeId != null) {
            return false;
          }
        } else if (Number(row.supervisorEmployeeId) !== Number(filters.supervisorEmployeeId)) {
          return false;
        }
      }
      if (filters.role) {
        if (filters.role === 'unassigned') {
          if (roleForRow(row)) return false;
        } else if (roleForRow(row) !== filters.role) {
          return false;
        }
      }
      if (filters.active !== 'any' && row.activeStatus !== filters.active) return false;
      if (filters.access === 'linked' && row.accessStatus === 'unlinked') return false;
      if (filters.access === 'unlinked' && row.accessStatus !== 'unlinked') return false;
      if (filters.access === 'active' && row.accessStatus !== 'active') return false;
      if (filters.access === 'inactive' && row.accessStatus !== 'inactive') return false;
      if (filters.certification === 'expiring' && row.certificationStatus !== 'expiring') return false;
      if (filters.certification === 'clear' && row.certificationStatus === 'expiring') return false;
      return true;
    });
  }, [filters, items, workforceOverlay]);

  // Auto-select was removed when the directory became an Operations
  // Console. The right pane intentionally shows the empty-state guide
  // when nothing is picked so operators see the workspace orientation
  // (and the URL stays at /employees, not /employees/profile/<id>).
  // A row click — or an external selectedManagerIdExternal from the
  // deep-link route — is the only way to populate the preview pane.

  // If the URL says we should be focused on an employee but that row is
  // absent from the loaded directory page (e.g. paginated beyond the
  // first window), we still honor selectedManagerIdExternal as the
  // single source of truth so refresh / deep link is preserved.

  const employeePathFor = (selection) => buildPersonWorkspacePath({
    employeeProfileId: selection?.profileId || null,
    managerId: selection?.managerId || null
  });

  const handleView = (row) => {
    if (!canDeepLink) return;
    const nextSelection = selectionFromRow(row);
    if (!nextSelection) return;
    setSelectedEmployee(nextSelection);
    recordEvent({ eventType: 'employee.select', properties: { source: 'click' } });
    if (nextSelection.profileId || nextSelection.managerId) {
      const path = employeePathFor(nextSelection);
      if (typeof onNavigate === 'function') {
        onNavigate(path, { preserveFilters: false });
      } else if (window.history?.replaceState) {
        window.history.replaceState({}, '', path);
      }
    }
  };

  if (status === 'forbidden') {
    return (
      <div className="employee-profile">
        <EmptyState
          icon="·"
          headline="Directory restricted"
          body="You need Employee Hub access to view this directory."
        />
      </div>
    );
  }

  // Sprint 3B.3: EmployeeDirectoryPage migrated to WorkspacePage with
  // width="operational" so the directory table + right-side profile
  // pane extend to the Sprint 3A 2200px shell on large monitors.
  // Header anatomy maps to:
  //   title       = focusCopy.title (per-focus directory heading)
  //   subtitle    = focusCopy.description
  //   actions     = "Add Employee" CTA (gated by canAddEmployee)
  //   contextSlot = People & Talent quick-link strip
  // The legacy .employee-profile.people-talent-workspace wrapper
  // survives as an inner element so the directory layout grid and
  // every existing employee-directory__* / people-talent-workspace__*
  // CSS selector keeps applying without a stylesheet rewrite.
  const headerActions = canAddEmployee ? (
    <div className="employee-directory__header-actions">
      <Button variant="secondary" onClick={() => setImportOpen(true)}>Import Employees</Button>
      <Button variant="primary" onClick={() => setAddOpen(true)}>Add Employee</Button>
    </div>
  ) : null;

  const headerQuicklinks = (
    <div className="people-talent-workspace__quicklinks" aria-label="People and Talent workspace links">
        {/* Quick pills are SPA navigations — use onNavigate so the app
            stays mounted and filter/period state isn't lost. <a href>
            triggers a full page reload, which lands on the right URL
            but resets workspace state and feels broken. */}
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setSelectedEmployee(null);
            if (typeof onNavigate === 'function') {
              setSelectedEmployee(null);
              onNavigate('/employees', { preserveFilters: false });
            }
          }}
          data-testid="people-talent-quicklink-directory"
          data-target-path="/employees"
        >
          Directory
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (typeof onNavigate === 'function') {
              onNavigate('/employees/expiring', { preserveFilters: false });
            }
          }}
          data-testid="people-talent-quicklink-expiring"
          data-target-path="/employees/expiring"
        >
          Expiring Certifications
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (typeof onNavigate === 'function') {
              onNavigate('/users', { preserveFilters: false });
            }
          }}
          data-testid="people-talent-quicklink-access"
          data-target-path="/users"
        >
          Access Admin
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (typeof onNavigate === 'function') {
              onNavigate('/managers', { preserveFilters: false });
            }
          }}
          data-testid="people-talent-quicklink-roster"
          data-target-path="/managers"
        >
          Leadership Directory
        </Button>
    </div>
  );

  return (
    <WorkspacePage
      data-workspace-id="employee-directory"
      data-testid="employee-directory-page"
      width="operational"
      title={focusCopy.title}
      subtitle={focusCopy.description}
      actions={headerActions}
      contextSlot={headerQuicklinks}
    >
      <div className="employee-profile people-talent-workspace">
        <div className="people-talent-workspace__layout">
        <aside className="people-talent-workspace__directory" aria-label="Employee directory">
          <ExpiringDocumentsPanel user={user} defaultExpanded={defaultExpiringExpanded} />

          <Card padding="sm" shadow="sm">
            <DirectoryFilters
              value={filters}
              onChange={setFilters}
              locations={locations}
              supervisorOptions={supervisorOptions}
            />
          </Card>

          {error ? <StatusBanner kind="error" message={error} onRetry={refetch} live /> : null}
          {overlayError ? <StatusBanner kind="warning" message={overlayError} live /> : null}
          {createBanner ? <StatusBanner kind="success" message={createBanner} live /> : null}

          <Card padding="none" shadow="sm">
            {isLoading && workforceRows.length === 0 ? (
              <div style={{ padding: 16 }}>
                <LoadingSkeleton variant="paragraph" lines={6} />
              </div>
            ) : workforceRows.length === 0 ? (
              <EmptyState
                compact
                icon="·"
                headline={filters.q ? 'No matches' : 'No workforce records yet'}
                body={filters.q
                  ? 'Try a different search or clear the filters.'
                  : 'People appear here from employee profiles, roster records, and linked user access.'}
              />
            ) : (
              <div className="people-talent-workspace__table-scroll">
                <table className="employee-directory__table" data-testid="employee-directory-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      {/* People Model Phase 4A — operator-facing Employee ID. */}
                      <th>Employee ID</th>
                      <th>Location</th>
                      <th>Role</th>
                      {/* Workforce People Model Phase 3.2 — Phase 1/2 fields. */}
                      <th>Job title</th>
                      <th>Department</th>
                      <th>Classification</th>
                      <th>Status</th>
                      <th aria-label="Row actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {workforceRows.map(row => (
                      <DirectoryRow
                        key={row.id}
                        row={row}
                        canDeepLink={canDeepLink}
                        isSelected={sameSelection(selectionFromRow(row), selectedEmployee)}
                        onView={handleView}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {nextCursor ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
              <Button variant="secondary" size="sm" onClick={loadMore} disabled={isLoading}>
                {isLoading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </aside>

        <main className="people-talent-workspace__hub" aria-label="Selected employee hub">
          {selectedEmployee ? (
            <EmployeeProfilePage
              managerIdOverride={selectedEmployee.managerId}
              profileIdOverride={selectedEmployee.profileId}
              embedded
              onNavigate={onNavigate || navigate}
              onProfileMutated={async () => {
                await refetch();
              }}
            />
          ) : (
            <Card padding="lg" shadow="sm" data-testid="people-talent-empty-hub">
              <EmptyState
                icon="·"
                headline="Select an employee to view their profile"
                body="The directory on the left lists every employee, manager, and access record this workspace can see. Pick a row to open the preview pane — profile, contact info, documents, and certifications live there. Use filters above to narrow by location, role, certification, or access status."
              />
              {workforceRows.length > 0 ? (
                <div className="people-talent-workspace__empty-hint" data-testid="people-talent-empty-hint">
                  Tip: keyboard the list with Tab + Enter, or click any row.
                </div>
              ) : null}
            </Card>
          )}
        </main>
      </div>
      {addOpen ? (
        <AddEmployeeModal
          locations={locations}
          profiles={(items || []).map(p => ({
            profileId: p.id,
            name: p.preferredName
              || [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
              || [p.managerFirstName, p.managerLastName].filter(Boolean).join(' ').trim()
              || p.name
              || null,
            preferredName: p.preferredName || null
          }))}
          onClose={() => setAddOpen(false)}
          onCreated={async ({ manager, profile }) => {
            setAddOpen(false);
            if (manager) {
              // Leadership assignment — navigate to the new roster member.
              setCreateBanner(`Employee created for ${manager.firstName} ${manager.lastName}. Employee ID: ${manager.employeeId}.`);
              await refetch();
              setSelectedEmployee({
                profileId: profile?.id ? Number(profile.id) : null,
                managerId: manager.id
              });
              const path = employeePathFor({ profileId: profile?.id ? Number(profile.id) : null, managerId: manager.id });
              if (typeof onNavigate === 'function') {
                onNavigate(path, { preserveFilters: false });
              } else if (window.history?.replaceState) {
                window.history.replaceState({}, '', path);
              }
            } else {
              // Standalone employee — no roster record to deep-link to.
              const name = profile?.preferredName || 'New employee';
              setCreateBanner(`Employee created: ${name}. Employee ID: ${profile?.employeeNumber || 'pending'}.`);
              await refetch();
              setSelectedEmployee(profile?.id ? { profileId: Number(profile.id), managerId: null } : null);
            }
          }}
        />
      ) : null}
      {importOpen ? (
        <ImportEmployeesModal
          onClose={() => setImportOpen(false)}
          onImported={async (result) => {
            setImportOpen(false);
            setCreateBanner(
              `Employees imported: ${result.summary.committed}. Employee IDs generated: ${result.summary.employeeIdsGenerated}. Locations affected: ${result.summary.locationsAffected}.`
            );
            await refetch();
          }}
          onSetupEnrollment={(result) => {
            setImportOpen(false);
            navigate(`/timekeeping/admin?importBatchId=${encodeURIComponent(result.importBatchId)}`);
          }}
        />
      ) : null}
      </div>
    </WorkspacePage>
  );
}
